import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  alpha,
  Box,
  Card,
  Chip,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { parseFloatToFixed } from "../../../../extras";
import { buttonVariants } from "../../../motion/buttons";

// Icons
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import StorefrontIcon from "@mui/icons-material/Storefront";
import DeliveryDiningIcon from "@mui/icons-material/DeliveryDining";
import ReceiptIcon from "@mui/icons-material/Receipt";
import LabeledIconCard from "../../../ui/cards/LabeledIconCard";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeliveryReportService from "../../../../api/report/deliveryReport";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CircularProgressWithLabel from "../../../indicators/CircularProgressWithLabel";
import { formatFinance } from "../../../../utils/formatters";

// Animation variants
const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
};

const DeliveryStats = ({ startDate, endDate }) => {
  const theme = useTheme();

  // Define states based on exactly what the Django API returns
  const [deliveryStats, setDeliveryStats] = useState({
    total_delivery_tax: 0,
    delivery_tax_delivery_guy_tax: 0,
    delivery_tax_difference: 0,
    total_delivered_orders: 0,
    total_shipping_routes: 0,
    total_delivery_shipping_route_orders: 0,
    orders_per_route: 0,
    past_30_days_orders_per_route: 0,
    percentage_change_in_efficiency: 0,
    delivery_fee_free_shipping_orders_count: 0,
    orders_delivered_by_ifood_count: 0,
    extra_deliveries: 0,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Effect to load data when component mounts or dates change
  useEffect(() => {
    loadData();
  }, [loadData, startDate, endDate]);

  // Load data function - using useCallback to prevent unnecessary recreations
  const loadData = useCallback(async () => {
    if (!startDate || !endDate) return;

    setLoading(true);
    setError(null);

    try {
      const params = {
        start_date: startDate,
        end_date: endDate,
      };

      const { data } = await DeliveryReportService.getPrimaryInfo(params);

      const inHouseDeliveryPercentage =
        data.total_delivered_orders > 0
          ? ((data.total_delivered_orders -
              (data?.orders_delivered_by_ifood_count ?? 0)) /
              data.total_delivered_orders) *
            100
          : 0;

      const freeDeliveryPercentage =
        data.total_delivered_orders > 0
          ? ((data?.delivery_fee_free_shipping_orders_count ?? 0) /
              data.total_delivered_orders) *
            100
          : 0;

      setDeliveryStats({
        ...data,
        inHouseDeliveryPercentage: inHouseDeliveryPercentage,
        freeDeliveryPercentage: freeDeliveryPercentage,
      });
    } catch (err) {
      console.error("Error loading order stats data:", err);
      setError("Não foi possível carregar os dados de pedidos");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  // Refresh data handler
  const handleRefresh = async () => {
    if (refreshing) return;

    setRefreshing(true);
    try {
      await loadData();
    } catch (e) {
      console.error("Error refreshing order data:", e);
      setError("Não foi possível atualizar os dados de pedidos");
    } finally {
      setRefreshing(false);
    }
  };

  // Stats items configuration - keep the colors only for the icons
  const statItems = [
    {
      title: "Tx Entregas",
      value: formatFinance(parseFloatToFixed(deliveryStats.total_delivery_tax)),
      icon: <AccessTimeIcon />,
      color: theme.palette.success.main,
    },
    {
      title: "Tx Entregas Entregadores",
      value: formatFinance(
        parseFloatToFixed(deliveryStats.delivery_tax_delivery_guy_tax) / 100
      ),
      icon: <AccessTimeIcon />,
      color: theme.palette.success.main,
    },
    {
      title: "Tx Entregas Diferença",
      value: formatFinance(
        parseFloatToFixed(deliveryStats.delivery_tax_difference)
      ),
      icon: <AccessTimeIcon />,
      color:
        deliveryStats.delivery_tax_difference > 0
          ? theme.palette.success.main
          : theme.palette.error.main,
    },
    {
      title: "Divider",
      value: "Entregas",
      icon: null,
      color: null,
    },
    {
      title: "Saídas de Entregador",
      value: deliveryStats.total_shipping_routes,
      icon: <ReceiptIcon />,
      color: theme.palette.primary.main,
    },
    {
      title: "Pedidos por Saída",
      value: parseFloatToFixed(deliveryStats.orders_per_route, 2),
      icon: <DeliveryDiningIcon />,
      color: theme.palette.info.main,
    },
    {
      title: "Pedidos Rotas",
      value: deliveryStats.total_delivery_shipping_route_orders,
      icon: <StorefrontIcon />,
      color: theme.palette.secondary.main,
    },
    {
      title: "Entrega Grátis",
      value: deliveryStats.delivery_fee_free_shipping_orders_count,
      icon: <PointOfSaleIcon />,
      color: theme.palette.success.main,
    },
    {
      title: "Divider",
      value: "Indicadores",
      icon: null,
      color: null,
    },
  ];

  return (
    <Box sx={{ height: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <ShowChartIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
          <Typography variant="h6" fontWeight={600}>
            Entregas
          </Typography>
        </Box>

        <motion.div whileHover="hover" whileTap="tap" variants={buttonVariants}>
          <Tooltip title="Atualizar dados">
            <IconButton
              size="small"
              onClick={handleRefresh}
              disabled={loading || refreshing}
              sx={{
                color: theme.palette.primary.main,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.2),
                },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </motion.div>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "300px",
          }}
        >
          <Typography variant="body1" color="text.secondary">
            Carregando dados...
          </Typography>
        </Box>
      ) : error ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "300px",
          }}
        >
          <Typography variant="body1" color="error">
            {error}
          </Typography>
        </Box>
      ) : (
        <Box>
          <Grid container spacing={1}>
            {statItems.map((item, index) => {
              if (item.title === "Divider") {
                return (
                  <Grid item xs={12} key={`stat-card-divider-${index}`}>
                    <Divider sx={{ my: 2 }}>
                      <Chip label={item.value} />
                    </Divider>
                  </Grid>
                );
              }

              return (
                <Grid item xs={6} sm={6} md={4} key={`stat-card-${index}`}>
                  <motion.div
                    variants={itemVariants}
                    initial="hidden"
                    animate="visible"
                    transition={{ delay: index * 0.05 }}
                  >
                    <LabeledIconCard
                      icon={item.icon}
                      title={item.title}
                      value={item.value}
                      color={item.color}
                    />
                  </motion.div>
                </Grid>
              );
            })}
            <Grid item xs={12}>
              <Card
                sx={{
                  p: 2,
                  borderLeft: `4px solid ${theme.palette.warning.main}`,
                  bgcolor: alpha(theme.palette.warning.main, 0.05),
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                  }}
                >
                  <WarningAmberIcon color="warning" />
                  <Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      sx={{ mb: 0.5 }}
                    >
                      Recomendação de Melhoria
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {deliveryStats?.orders_per_route < 1.5
                        ? "Sua média de pedidos por rota está abaixo do ideal. Considere otimizar rotas agrupando entregas por região para aumentar a eficiência."
                        : "Seus indicadores de entrega estão em bom estado. Continue monitorando para manter o desempenho."}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>
          </Grid>
          <Box sx={{ p: 2 }}>
            <Grid container spacing={3}>
              {/* Delivery Source Distribution */}
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    p: 2,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ mb: 3 }}
                  >
                    Fonte das Entregas
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      gap: 4,
                      mb: 2,
                      alignItems: "center",
                    }}
                  >
                    <Box
                      component={motion.div}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.5 }}
                      sx={{ textAlign: "center" }}
                    >
                      <CircularProgressWithLabel
                        value={Math.round(
                          deliveryStats?.inHouseDeliveryPercentage
                        )}
                        color={theme.palette.success.main}
                        label="Próprias"
                      />
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ mt: 1, color: theme.palette.success.main }}
                      >
                        Próprias
                      </Typography>
                    </Box>

                    <Box
                      component={motion.div}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.5, delay: 0.2 }}
                      sx={{ textAlign: "center" }}
                    >
                      <CircularProgressWithLabel
                        value={Math.round(
                          100 - deliveryStats?.inHouseDeliveryPercentage
                        )}
                        color={theme.palette.error.main}
                        label="iFood"
                      />
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ mt: 1, color: theme.palette.error.main }}
                      >
                        iFood
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ width: "100%", mt: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 0.5,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Entregas próprias
                      </Typography>
                      <Typography variant="caption" fontWeight={600}>
                        {Math.round(deliveryStats?.inHouseDeliveryPercentage)}%
                        (
                        {deliveryStats?.total_delivered_orders -
                          (deliveryStats?.orders_delivered_by_ifood_count ?? 0)}
                        )
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 0.5,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Entregas iFood
                      </Typography>
                      <Typography variant="caption" fontWeight={600}>
                        {Math.round(
                          100 - deliveryStats?.inHouseDeliveryPercentage
                        )}
                        % ({deliveryStats?.orders_delivered_by_ifood_count ?? 0}
                        )
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>

              {/* Free vs Paid Deliveries */}
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    p: 2,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ mb: 3 }}
                  >
                    Entregas Grátis vs Pagas
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      gap: 4,
                      mb: 2,
                      alignItems: "center",
                    }}
                  >
                    <Box
                      component={motion.div}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.5 }}
                      sx={{ textAlign: "center" }}
                    >
                      <CircularProgressWithLabel
                        value={Math.round(
                          100 - deliveryStats?.freeDeliveryPercentage
                        )}
                        color={theme.palette.success.main}
                        label="Pagas"
                      />
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ mt: 1, color: theme.palette.success.main }}
                      >
                        Pagas
                      </Typography>
                    </Box>

                    <Box
                      component={motion.div}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.5, delay: 0.2 }}
                      sx={{ textAlign: "center" }}
                    >
                      <CircularProgressWithLabel
                        value={Math.round(
                          deliveryStats?.freeDeliveryPercentage
                        )}
                        color={theme.palette.warning.main}
                        label="Grátis"
                      />
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ mt: 1, color: theme.palette.warning.main }}
                      >
                        Grátis
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ width: "100%", mt: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 0.5,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Entregas pagas
                      </Typography>
                      <Typography variant="caption" fontWeight={600}>
                        {Math.round(
                          100 - deliveryStats?.freeDeliveryPercentage
                        )}
                        % (
                        {deliveryStats?.total_delivered_orders -
                          (deliveryStats?.delivery_fee_free_shipping_orders_count ??
                            0)}
                        )
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 0.5,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Entregas grátis
                      </Typography>
                      <Typography variant="caption" fontWeight={600}>
                        {Math.round(deliveryStats?.freeDeliveryPercentage)}% (
                        {deliveryStats?.delivery_fee_free_shipping_orders_count ??
                          0}
                        )
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>

              {/* Advice Card */}
              <Grid item xs={12}>
                <Card
                  sx={{
                    p: 2,
                    borderLeft: `4px solid ${theme.palette.info.main}`,
                    bgcolor: alpha(theme.palette.info.main, 0.05),
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                    }}
                  >
                    <InfoOutlinedIcon color="info" />
                    <Box>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        sx={{ mb: 0.5 }}
                      >
                        Insight: Otimização de Entregas
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {deliveryStats?.freeDeliveryPercentage > 25
                          ? "A taxa de entregas grátis está alta (>25%). Considere rever a política de frete grátis para melhorar margens."
                          : "Suas entregas próprias representam a maioria. Continue otimizando rotas para aumentar a eficiência."}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DeliveryStats;
